import React, { useState } from "react";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import { InputOutput } from "../../InputOutput";
import { ExpedienteNode } from "./expediente-node";
import { ExpedienteNodeProvider, useExpedienteNode } from "./context";
import { Icon } from "@elaniintc/goes-design-system";
import Variables from "@/workflow_api/components/variables";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

// Map entity field_type to variable type for display
const mapFieldTypeToVariableType = (fieldType: string): string => {
  switch (fieldType) {
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "select":
    case "radio":
      return "str";
    case "number":
    case "currency":
      return "num";
    case "date":
    case "datetime":
      return "date";
    case "checkbox":
      return "bool";
    case "file":
    case "image":
      return "file";
    case "list":
      return "array";
    default:
      return "str";
  }
};

export const ExpedienteDetailsForm: React.FC = () => {
  const {
    nodeDetailsOpen,
    setNodeDetailsOpen,
    setSelectedNodeId,
    selectedNodeId,
  } = useUIStore();
  const { getNodeById } = useWorkflowStore();
  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;
  const [title, setTitle] = useState<string>(
    nodeData.displayName || "Expediente",
  );
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);

  const {
    selectedAction,
    isReadyToShow,
    columnsAreValid,
    saveNode,
    entityFields,
    isLoadingEntityFields,
    variableName,
    // New entity/registry selection
    selectedEntity,
    registryInputMode,
    registryIdValue,
    registryVariable,
    // Link users validation
    linkUsersAreValid,
    // Link expedients validation
    linkExpedientsAreValid,
    linkSubAction,
  } = useExpedienteNode();

  // Compute if registry ID is provided (either as input or variable)
  const hasRegistryValue =
    registryInputMode === "inp" ? !!registryIdValue.trim() : !!registryVariable;

  // Convert entity fields to variables for InputOutput component
  const outputVariables = React.useMemo(() => {
    if (!entityFields?.length) {
      return undefined;
    }
    return entityFields.map((field) => ({
      name: field.name,
      type: mapFieldTypeToVariableType(field.field_type),
    }));
  }, [entityFields]);

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        isSaveValid={
          isReadyToShow &&
          !!selectedEntity &&
          (selectedAction?.id === "create" || hasRegistryValue) &&
          !!selectedAction &&
          // For "read" and "deactivate", only entity, registry ID, and action are required
          (["read", "deactivate"].includes(selectedAction.id)
            ? true
            : // For "link_users", check based on linkSubAction
              selectedAction.id === "link_users"
              ? linkSubAction === "link_users"
                ? linkUsersAreValid
                : linkExpedientsAreValid
              : // For "create" and "update", only need columns to be valid (registry name is optional)
                columnsAreValid) &&
          isNameValid(title)
        }
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={() => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          saveNode(title);
          setNodeDetailsOpen(false);
          setSelectedNodeId();
        }}
      >
        <ThreeColumnLayout
          className="overflow-auto"
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            icon: "folder-open",
            title: title,
            color: "info",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Output",
            color: "neutral",
          }}
          leftContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                <Variables sourceNodeId={selectedNodeId!} />
              </div>
            </>
          }
          leftContentWidth="min-w-[445px]"
          centerContent={
            isReadyToShow ? (
              <ExpedienteNode />
            ) : (
              <div className="min-h-[48px] text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
                <Icon
                  type="far"
                  name="spinner-third"
                  className="animate-spin text-xl text-body"
                />
              </div>
            )
          }
          rightContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                <InputOutput
                  nodeType={NodeTypes.EXPEDIENTE}
                  showSteps={false}
                  nodeLabel={title}
                  variableName={variableName || "ID"}
                  isLoading={!isReadyToShow || isLoadingEntityFields}
                  params={{
                    operation: selectedAction?.id,
                  }}
                  variables={outputVariables}
                  variableType={
                    outputVariables && outputVariables.length > 0
                      ? undefined
                      : "str"
                  }
                />
              </div>
            </>
          }
        />
      </FullModal>
    </>
  );
};

export const ExpedienteDetails: React.FC = () => {
  return (
    <ExpedienteNodeProvider>
      <ExpedienteDetailsForm />
    </ExpedienteNodeProvider>
  );
};
